import React from "react";
import { Box, Flex, Modal, ModalBody, ModalContent, ModalOverlay, useDisclosure } from "@chakra-ui/react";
import { VideoJsonLd } from "next-seo";
import { trackVideoEnded, trackVideoPlayed } from "components/event-tracking";
import { VideoPlayIcon } from "ui/icons";
import { type Theme } from "contexts/color-theme";

type VideoModalProps = {
  /** Shortened, separate video source to be used as b-roll for the card */
  shortSrc: string;
  /** The video source URL, should be an mp4 file */
  src: string;
  /** The path to the `.vtt` file containing video captions */
  captions?: string;
  /** The video title, used for SEO metadata */
  title: string;
  /** The video description, used for SEO metadata */
  description: string;
  /** The video upload date, used for SEO metadata */
  uploadDate: string;
  /** Video thumbnail image URL, used for SEO metadata */
  thumbnail: string;
  /** The video ID, used for tracking events */
  videoId: string;
  /**
   * The theme of the play icon
   * @default "light"
   */
  iconTheme?: Theme;
};

export function VideoModal({
  shortSrc,
  src,
  captions,
  title,
  description,
  uploadDate,
  thumbnail,
  videoId,
  iconTheme = "light",
}: VideoModalProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const formattedDate = new Date(uploadDate).toISOString();
  const iconColor = iconTheme === "light" ? "white" : "brand.textSecondary.light";

  return (
    <Box boxSize="100%">
      <Flex align="center" boxSize="100%" justify="center" pos="relative">
        <video autoPlay loop muted playsInline width="100%" height="100%" preload="metadata" poster={thumbnail}>
          <source src={shortSrc} type="video/mp4" />
        </video>

        <Flex
          as="button"
          aria-label="Open video modal and play video"
          align="center"
          borderRadius="full"
          border="1px solid"
          borderColor="transparent"
          color={iconColor}
          justify="center"
          cursor="pointer"
          pos="absolute"
          opacity={0.6}
          overflow="hidden"
          outline="none"
          zIndex={10}
          transition="all 0.15s ease-in-out"
          _hover={{ opacity: 0.9 }}
          _focusVisible={{ opacity: 0.9, boxShadow: "outline" }}
          onClick={onOpen}
        >
          <VideoPlayIcon boxSize={{ base: "40px", sm: "60px" }} />
        </Flex>
      </Flex>

      <Modal isCentered size="6xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(8px)" />
        <ModalContent m="small">
          <ModalBody p={0}>
            <video
              autoPlay
              controls
              playsInline
              width="100%"
              height="100%"
              preload="metadata"
              controlsList="nodownload"
              onPlay={() => {
                trackVideoPlayed(videoId);
              }}
              onEnded={() => {
                trackVideoEnded(videoId);
              }}
            >
              <source src={src} type="video/mp4" />
              {captions && <track kind="captions" srcLang="en-us" label="English" src={captions} />}
            </video>
          </ModalBody>
        </ModalContent>
      </Modal>

      <VideoJsonLd
        name={title}
        description={description}
        contentUrl={src}
        embedUrl={src}
        thumbnailUrls={[thumbnail]}
        uploadDate={formattedDate}
      />
    </Box>
  );
}
